/**
 * Private Vault cryptography (Web Crypto API).
 *
 * A passphrase the member types is stretched with PBKDF2-SHA256 into a 256-bit
 * AES-GCM key. The passphrase and the derived key NEVER leave the browser — the
 * server only ever sees the salt, a verifier blob, and opaque ciphertext.
 *
 * The key is held in memory while the vault is unlocked and dropped on lock /
 * logout. It is deliberately never written to localStorage or sessionStorage.
 */

/**
 * How a passphrase is stretched into a key.
 *
 * The figure travels WITH each vault rather than living only here, so it can
 * be raised later without stranding vaults created under the old one:
 * unlocking derives with the parameters the server hands back, and only a new
 * vault or a rekey uses CURRENT_KDF. Before this existed the constant below
 * was the whole story, and changing it would have locked every member out of
 * their own notes.
 *
 * 310,000 sits below OWASP's current 600,000 for PBKDF2-HMAC-SHA256. Raising
 * it is now a one-line change here and the matching default in
 * vault_router.py -- but it should not happen without measuring the unlock
 * delay on the oldest phone in the Society, because that cost is paid by a
 * member waiting to read their own notes.
 */
export const CURRENT_KDF = { alg: 'PBKDF2-SHA256', iterations: 310000 };

// What a vault written before the parameters were recorded actually used.
const LEGACY_ITERATIONS = 310000;

function iterationsFrom(kdf) {
  const n = kdf && Number(kdf.iterations);
  return Number.isFinite(n) && n > 0 ? n : LEGACY_ITERATIONS;
}

const VERIFIER_PLAINTEXT = 'arca-vault-v1';

function bytesToB64(bytes) {
  let bin = '';
  const arr = new Uint8Array(bytes);
  for (let i = 0; i < arr.length; i++) bin += String.fromCharCode(arr[i]);
  return btoa(bin);
}

function b64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function isSupported() {
  return typeof window !== 'undefined' && window.crypto && window.crypto.subtle && window.isSecureContext;
}

export function randomSaltB64() {
  return bytesToB64(window.crypto.getRandomValues(new Uint8Array(16)));
}

/**
 * Stretch a passphrase into an AES-GCM key. Intentionally slow.
 *
 * `kdf` is the descriptor stored with the vault. Omitting it means a vault
 * from before the parameters were recorded, which used the legacy figure.
 */
export async function deriveKey(passphrase, saltB64, kdf) {
  const base = await window.crypto.subtle.importKey(
    'raw', new TextEncoder().encode(passphrase), 'PBKDF2', false, ['deriveKey']
  );
  return window.crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: b64ToBytes(saltB64), iterations: iterationsFrom(kdf), hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

/** Encrypt a JS value (object or string) → { iv, ciphertext } base64. */
export async function encryptJSON(key, value) {
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const data = new TextEncoder().encode(JSON.stringify(value));
  const ct = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, data);
  return { iv: bytesToB64(iv), ciphertext: bytesToB64(ct) };
}

/** Decrypt back into the original value. Throws if the key is wrong. */
export async function decryptJSON(key, ivB64, ciphertextB64) {
  const pt = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: b64ToBytes(ivB64) }, key, b64ToBytes(ciphertextB64)
  );
  return JSON.parse(new TextDecoder().decode(pt));
}

/** Build the blob that lets us recognise a correct passphrase later. (scheme 1) */
export async function makeVerifier(key) {
  const { iv, ciphertext } = await encryptJSON(key, VERIFIER_PLAINTEXT);
  return { verifier_iv: iv, verifier_ciphertext: ciphertext };
}

/* ------------------------------------------------------------------------ *
 * Scheme 2 — envelope encryption.
 *
 * A random master key encrypts the notes. That master key is then sealed twice:
 * once with a key derived from the passphrase, once with a high-entropy
 * recovery key the member writes down. Forgetting the passphrase is survivable;
 * changing it doesn't require re-encrypting a single note.
 * ------------------------------------------------------------------------ */

/** Random 256-bit master key (extractable so it can be wrapped). */
export async function generateMasterKey() {
  return window.crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
}

/** Human-friendly recovery key: 32 random bytes in base32-ish groups. */
export function generateRecoveryKey() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no look-alikes
  const bytes = window.crypto.getRandomValues(new Uint8Array(20));
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += alphabet[bytes[i] % alphabet.length];
    if ((i + 1) % 4 === 0 && i !== bytes.length - 1) out += '-';
  }
  return out; // e.g. K7QM-3PXA-9DFH-2RTV-8LWC
}

/** Turn a recovery key string into an AES-GCM key (stretched with a fixed-ish salt). */
export async function deriveRecoveryKek(recoveryKey, saltB64, kdf) {
  const normalized = (recoveryKey || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  return deriveKey(`recovery:${normalized}`, saltB64, kdf);
}

/** Seal the master key with a wrapping key. */
export async function wrapMasterKey(masterKey, wrappingKey) {
  const raw = await window.crypto.subtle.exportKey('raw', masterKey);
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const ct = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrappingKey, raw);
  return { iv: bytesToB64(iv), ciphertext: bytesToB64(ct) };
}

/** Open a sealed master key. Throws if the wrapping key is wrong. */
export async function unwrapMasterKey(wrapped, wrappingKey) {
  const raw = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: b64ToBytes(wrapped.iv) }, wrappingKey, b64ToBytes(wrapped.ciphertext)
  );
  return window.crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}

/** True when the derived key can open the stored verifier. */
export async function checkVerifier(key, ivB64, ciphertextB64) {
  try {
    const value = await decryptJSON(key, ivB64, ciphertextB64);
    return value === VERIFIER_PLAINTEXT;
  } catch (e) {
    return false;
  }
}
